import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, Compass, PencilRuler, BarChart3, Send, Check } from "lucide-react";
import { Brand, Eyebrow, EqualMark } from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What are the Long Beach sensitive-use buffer rules for a dispensary?",
  "How do I tell a hard \u201cno cannabis\u201d rejection from a \u201cwrong tenant mix\u201d one?",
  "What should I check when touring a commercial space?",
  "How do I write a simple letter of intent for a small cannabis business?",
];

const cfg = (token) => ({ withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });

export default function ApplicantHome() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const email = localStorage.getItem("email");

  const [me, setMe] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [aiInput, setAiInput] = useState("");

  useEffect(() => {
    const prev = document.title; document.title = "Equal — Home";
    axios.get(`${API}/auth/me`, cfg(token)).then((r) => setMe(r.data)).catch(() => setMe(null));
    axios.get(`${API}/roadmap`, cfg(token)).then((r) => setRoadmap(r.data)).catch(() => setRoadmap(null));
    return () => { document.title = prev; };
    // eslint-disable-next-line
  }, []);

  const firstName = useMemo(() => {
    const n = (me && me.name) || "";
    if (n) return n.trim().split(/\s+/)[0];
    if (email && email.includes("@")) {
      const p = email.split("@")[0].replace(/[._-]+/g, " ").trim();
      return p ? p.split(" ")[0].replace(/^\w/, (c) => c.toUpperCase()) : "there";
    }
    return "there";
  }, [me, email]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  }, []);

  const askAI = (q) => {
    const text = (typeof q === "string" ? q : aiInput).trim();
    if (text) localStorage.setItem("equal_chat_prefill", text);
    else localStorage.removeItem("equal_chat_prefill");
    navigate("/chat");
  };

  const firstTime = roadmap && !roadmap.business_type && roadmap.progress && roadmap.progress.done === 0;
  const pct = roadmap && roadmap.progress.total ? Math.round((roadmap.progress.done / roadmap.progress.total) * 100) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-page" data-testid="applicant-home">
      <header className="flex items-center justify-between gap-4 px-5 md:px-7 h-14 bg-white border-b border-line">
        <Brand hint="Cannabis Equity Advisory" />
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-mute text-xs font-mono">{email}</span>
          <button onClick={() => { localStorage.clear(); navigate("/"); }} className="btn-quiet btn-sm" data-testid="home-logout">Sign out</button>
        </div>
      </header>

      <main className="flex-1 w-full max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {/* Greeting */}
        <div className="animate-rise-in">
          <Eyebrow className="text-equal-dark">Welcome to Equal</Eyebrow>
          <h1 className="mt-2 text-3xl md:text-[2.6rem] font-extrabold tracking-tight text-ink leading-[1.1]" data-testid="home-greeting">
            {greeting}, {firstName}.
          </h1>
          <p className="mt-3 text-mute text-base leading-relaxed max-w-xl">
            {firstTime
              ? "Let's get you started — ask the AI Advisor anything, or set up your property roadmap in a minute."
              : "Pick up where you left off, or ask the AI Advisor your next question."}
          </p>
        </div>

        {/* Featured AI Advisor — the hero */}
        <section className="mt-8 card-flat overflow-hidden animate-rise-in" style={{ animationDelay: "60ms" }} data-testid="home-ai-hero">
          <div className="bg-mint px-6 md:px-8 pt-7 pb-6 border-b border-[#bfe6cf]">
            <div className="flex items-center gap-2 text-equal-dark">
              <span className="w-8 h-8 rounded-[9px] bg-white border border-[#bfe6cf] grid place-items-center flex-none"><EqualMark size={18} /></span>
              <Eyebrow className="text-equal-dark">AI Advisor</Eyebrow>
            </div>
            <h2 className="mt-3 text-2xl md:text-[1.7rem] font-bold text-ink tracking-tight leading-tight">
              Ask anything about finding &amp; securing your space.
            </h2>
            <p className="mt-2 text-ink-2 text-sm leading-relaxed max-w-2xl">
              Zoning buffers, leasing, letters of intent, working with brokers — grounded in Long Beach rules and real program sessions.
            </p>
            <div className="mt-5 flex items-stretch gap-2 max-w-2xl">
              <input
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") askAI(); }}
                placeholder="Ask the AI Advisor a question…"
                data-testid="home-ai-input"
                className="flex-1 px-4 py-3 rounded-[11px] border border-[#bfe6cf] bg-white text-ink text-[15px] placeholder:text-mute focus:outline-none focus:ring-2 focus:ring-equal/25"
              />
              <button onClick={() => askAI()} className="btn-primary px-4" data-testid="home-ai-send" aria-label="Ask the AI Advisor">
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline ml-1">Ask</span>
              </button>
            </div>
          </div>
          <div className="px-6 md:px-8 py-5">
            <p className="eyebrow mb-3">Try asking</p>
            <div className="flex flex-wrap gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => askAI(s)} data-testid={`home-ai-suggestion-${i}`}
                  className="text-left text-sm text-ink-2 bg-page hover:bg-white hover:border-equal border border-line rounded-full px-4 py-2 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Your next step / start roadmap */}
        {roadmap && (
          firstTime ? (
            <section className="mt-6 card-flat p-6 md:p-7 flex flex-col sm:flex-row sm:items-center gap-5 animate-rise-in" style={{ animationDelay: "120ms" }} data-testid="home-start-roadmap">
              <span className="w-11 h-11 rounded-[11px] bg-mint grid place-items-center flex-none"><Compass className="w-5 h-5 text-equal-dark" /></span>
              <div className="min-w-0 flex-1">
                <Eyebrow className="text-equal-dark">Start your Property Roadmap</Eyebrow>
                <p className="mt-1.5 text-ink font-semibold text-[15px]">Pick your business type to get a step-by-step plan.</p>
                <p className="text-mute text-sm mt-0.5">A guided path from defining what you need to a signed lease.</p>
              </div>
              <button onClick={() => navigate("/roadmap")} className="btn-primary btn-sm flex-none" data-testid="home-start-roadmap-btn">
                Start now <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </section>
          ) : (
            <section className="mt-6 card-flat p-6 md:p-7 animate-rise-in" style={{ animationDelay: "120ms" }} data-testid="home-next-step">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-equal-dark">
                    {roadmap.finished ? <Check className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />}
                    <Eyebrow className="text-equal-dark">{roadmap.finished ? "Property roadmap · complete" : "Your next step"}</Eyebrow>
                  </div>
                  <p className="mt-2 text-ink font-bold text-lg tracking-tight" data-testid="home-next-step-title">
                    {roadmap.finished
                      ? "Space secured — your roadmap is complete."
                      : (roadmap.next_step && roadmap.next_step.task.title)}
                  </p>
                  {!roadmap.finished && roadmap.next_step && roadmap.next_step.task.why && (
                    <p className="mt-1 text-mute text-sm leading-relaxed max-w-xl">{roadmap.next_step.task.why}</p>
                  )}
                </div>
                <button onClick={() => navigate("/roadmap")} className="btn-ghost btn-sm flex-none" data-testid="home-next-step-open">
                  Open roadmap <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <div className="h-2 flex-1 max-w-xs rounded-full bg-line overflow-hidden">
                  <div className="h-full bg-equal rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <span className="font-mono text-xs text-mute">{roadmap.progress.done} of {roadmap.progress.total} steps</span>
              </div>
            </section>
          )
        )}

        {/* Secondary tools */}
        <div className="mt-8">
          <p className="eyebrow mb-3">Your tools</p>
          <div className="grid sm:grid-cols-3 gap-4">
            <ToolCard testid="home-tool-roadmap" icon={Compass} title="Property Roadmap"
              desc="Your step-by-step path to a signed lease." onClick={() => navigate("/roadmap")} />
            <ToolCard testid="home-tool-studio" icon={PencilRuler} title="Design Studio"
              desc="Plan a compliant floor plan and visualize your space." onClick={() => navigate("/studio")} />
            <ToolCard testid="home-tool-comps" icon={BarChart3} title="Market Data"
              desc="Compare real Long Beach lease deals and rents." onClick={() => navigate("/comps")} />
          </div>
        </div>
      </main>
    </div>
  );
}

const ToolCard = ({ testid, icon: Icon, title, desc, onClick }) => (
  <button onClick={onClick} data-testid={testid}
    className="group card-flat p-5 text-left flex flex-col gap-3 hover:border-equal hover:shadow-sm transition-all h-full">
    <span className="w-10 h-10 rounded-[10px] bg-mint grid place-items-center"><Icon className="w-5 h-5 text-equal-dark" /></span>
    <div className="flex-1">
      <div className="flex items-center gap-1.5">
        <span className="font-bold text-ink text-[15px] tracking-tight">{title}</span>
        <ArrowRight className="w-3.5 h-3.5 text-mute -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
      </div>
      <p className="text-mute text-sm mt-1 leading-relaxed">{desc}</p>
    </div>
  </button>
);
